/**
 * Phase 12 Plan 04 (SIGNSAFE-05, D-19) — shared data:image redaction.
 *
 * The drawn signature leaves the pad as a `data:image/png;base64,...` string.
 * It may only ever travel in the single work-confirmation POST body. Telemetry
 * has real leak paths for it (Sentry console breadcrumbs carry the logged
 * arguments plus a joined message, Vue error props attach to `contexts.vue`,
 * Datadog RUM collects console errors), so both the Sentry and the Datadog
 * plugins route their payload strings through this module.
 *
 * Two regexes on purpose (RESEARCH anti-pattern): a NON-global regex for
 * detection, because a `/g` regex keeps `lastIndex` between `.test()` calls and
 * skips matches on the next call, and a separate global regex for `replace`.
 */
export const DATA_IMAGE_DETECT = /data:image\//i;
const DATA_IMAGE_ALL = /data:image\/[a-z0-9.+-]+;base64,[a-z0-9+/=]*/gi;
const SCRUBBED = '[scrubbed]';
// Well past any real payload shape; a guard against pathological nesting only.
const MAX_DEEP_DEPTH = 32;

/**
 * Replace every base64 data:image run in `s` with `[scrubbed]`. If any
 * `data:image/` survives the targeted replace (for example a non-base64
 * `data:image/svg+xml,...` URL), the whole string is scrubbed: failing closed
 * matters more than keeping the surrounding text.
 */
export function redactDataImages(s: string): string {
  if (!DATA_IMAGE_DETECT.test(s)) return s;
  const replaced = s.replace(DATA_IMAGE_ALL, SCRUBBED);
  return DATA_IMAGE_DETECT.test(replaced) ? SCRUBBED : replaced;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return false;
  }
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

function walk(value: unknown, seen: WeakSet<object>, depth: number): unknown {
  if (typeof value === 'string') return redactDataImages(value);
  const isArray = Array.isArray(value);
  if (!isArray && !isPlainObject(value)) return value;
  const obj = value as object;
  // Cycle or runaway depth: drop the branch rather than recurse forever. The
  // placeholder can never carry a signature, which is the guarantee we need.
  if (seen.has(obj) || depth >= MAX_DEEP_DEPTH) return SCRUBBED;
  seen.add(obj);
  const out = isArray
    ? (value as unknown[]).map((item) => walk(item, seen, depth + 1))
    : Object.fromEntries(
        Object.entries(value as Record<string, unknown>).map(([k, v]) => [
          k,
          walk(v, seen, depth + 1),
        ]),
      );
  seen.delete(obj);
  return out;
}

/**
 * Return a NEW value with every string redacted at any depth. Plain objects and
 * arrays are walked (independent of the Sentry key scrubber's `MAX_DEPTH = 4`);
 * a cycle is cut with a WeakSet guard and nesting is capped at 32 levels. Other
 * values (numbers, class instances, functions) are returned as-is.
 */
export function redactDataImagesDeep<T>(value: T): T {
  return walk(value, new WeakSet<object>(), 0) as T;
}
